import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDateTime } from "@/lib/format";
import { toast } from "@/hooks/use-toast";
import {
  selectionState,
  toggleVisibleSelection,
} from "@shared/file-selection";
import {
  Archive,
  Download,
  FolderPlus,
  Pencil,
  Settings,
  Trash2,
} from "lucide-react";
import { Link } from "wouter";

type ClientRow = { clientId: string; clientName: string };
type Folder = {
  id: string;
  name: string;
  kind: string;
  isPartnerFolder?: boolean;
};
type ClientFile = {
  id: string;
  relativePath: string;
  fileName: string;
  sizeBytes: number;
  createdAt: string;
  folderId: string;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function describeError(error: unknown): string {
  const message = error instanceof Error ? error.message : "Something went wrong";
  const jsonStart = message.indexOf("{");
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(message.slice(jsonStart)) as { message?: unknown };
      if (typeof parsed.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      // Fall through to the raw message.
    }
  }
  return message.replace(/^\d+:\s*/, "");
}

function submitNativeZipDownload(clientId: string, fileIds: string[]) {
  const frameName = "admin-files-zip-download";
  let frame = document.querySelector<HTMLIFrameElement>(
    `iframe[name="${frameName}"]`,
  );
  if (!frame) {
    frame = document.createElement("iframe");
    frame.name = frameName;
    frame.hidden = true;
    document.body.appendChild(frame);
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/api/files/download-zip";
  form.target = frameName;
  const add = (name: string, value: string) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };
  add("clientId", clientId);
  for (const fileId of fileIds) add("fileIds", fileId);
  document.body.appendChild(form);
  form.submit();
  form.remove();
}

export default function AdminFilesPage() {
  const params = new URLSearchParams(window.location.search);
  const [clientId, setClientId] = useState(params.get("clientId") || "");
  const [folderId, setFolderId] = useState(params.get("folderId") || "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newFolderName, setNewFolderName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [removeOpen, setRemoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [zipPending, setZipPending] = useState(false);

  const { data: clients = [] } = useQuery<ClientRow[]>({
    queryKey: ["/api/files/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: folders = [] } = useQuery<Folder[]>({
    queryKey: ["/api/files/folders", clientId],
    queryFn: async () => {
      const res = await fetch(
        `/api/files/folders?clientId=${encodeURIComponent(clientId)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: !!clientId,
  });

  const activeFolderId = useMemo(() => {
    if (folderId && folders.some((folder) => folder.id === folderId)) return folderId;
    return folders.find((folder) => folder.kind === "inbox")?.id || folders[0]?.id || "";
  }, [folderId, folders]);

  const activeFolder = folders.find((folder) => folder.id === activeFolderId) ?? null;
  const canRenameFolder = activeFolder?.kind === "custom";
  const canRemoveFolder = activeFolder?.kind === "custom";

  useEffect(() => {
    setSelected(new Set());
  }, [clientId, activeFolderId]);

  const { data: files = [], isLoading } = useQuery<ClientFile[]>({
    queryKey: ["/api/files/files", clientId, activeFolderId],
    queryFn: async () => {
      const qs = new URLSearchParams({
        clientId,
        ...(activeFolderId ? { folderId: activeFolderId } : {}),
      });
      const res = await fetch(`/api/files/files?${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: !!clientId && !!activeFolderId,
  });

  const visibleIds = useMemo(() => files.map((file) => file.id), [files]);
  const headerSelection = selectionState(visibleIds, selected);

  const createFolderMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/files/folders", {
        clientId,
        name: newFolderName,
      });
      return res.json();
    },
    onSuccess: async () => {
      setNewFolderName("");
      await queryClient.invalidateQueries({
        queryKey: ["/api/files/folders", clientId],
      });
      toast({ title: "Folder created" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not create folder",
        description: describeError(error),
        variant: "destructive",
      });
    },
  });

  const renameFolderMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/files/folders/${activeFolderId}`, {
        clientId,
        name: renameValue,
      });
      return res.json();
    },
    onSuccess: async () => {
      setRenameOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["/api/files/folders", clientId],
      });
      toast({ title: "Folder renamed" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not rename folder",
        description: describeError(error),
        variant: "destructive",
      });
    },
  });

  const removeFolderMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "DELETE",
        `/api/files/folders/${activeFolderId}?clientId=${encodeURIComponent(clientId)}`,
      );
      return res;
    },
    onSuccess: async () => {
      setRemoveOpen(false);
      setFolderId("");
      await queryClient.invalidateQueries({
        queryKey: ["/api/files/folders", clientId],
      });
      toast({ title: "Folder removed" });
    },
    onError: (error: Error) => {
      setRemoveOpen(false);
      toast({
        title: "Could not remove folder",
        description: describeError(error),
        variant: "destructive",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/files/move-to-archive", {
        clientId,
        fileIds: Array.from(selected),
      });
      return res.json();
    },
    onSuccess: async () => {
      setSelected(new Set());
      await queryClient.invalidateQueries({
        queryKey: ["/api/files/files", clientId],
      });
      toast({ title: "Moved to Archive" });
    },
    onError: (error: Error) => {
      toast({
        title: "Archive failed",
        description: describeError(error),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/files/files/delete", {
        clientId,
        fileIds: Array.from(selected),
      });
      return res.json() as Promise<{
        deletedIds: string[];
        failed: Array<{ id: string; message: string }>;
      }>;
    },
    onSuccess: async (result) => {
      setDeleteOpen(false);
      setSelected(new Set());
      await queryClient.invalidateQueries({
        queryKey: ["/api/files/files", clientId],
      });
      toast({
        title: `Deleted ${result.deletedIds.length} file${result.deletedIds.length === 1 ? "" : "s"}`,
        description: result.failed.length
          ? `${result.failed.length} file${result.failed.length === 1 ? "" : "s"} could not be deleted.`
          : "This cannot be undone.",
        variant: result.failed.length ? "destructive" : "default",
      });
    },
    onError: (error: Error) => {
      setDeleteOpen(false);
      toast({
        title: "Delete failed",
        description: describeError(error),
        variant: "destructive",
      });
    },
  });

  const applyCorsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/files/cors/apply", {
        origins: ["https://service.heyhihello.com"],
      });
      return res.json() as Promise<{
        origins: string[];
        allowedHeaders: string[];
        allowedMethods: string[];
      }>;
    },
    onSuccess: (data) => {
      toast({
        title: "R2 CORS updated",
        description: `Origins: ${data.origins.join(", ")}. Allowed headers include content-type.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not apply R2 CORS",
        description: describeError(error),
        variant: "destructive",
      });
    },
  });

  const downloadFile = async (fileId: string) => {
    const res = await fetch(
      `/api/files/files/${fileId}/download-url?clientId=${encodeURIComponent(clientId)}`,
      { credentials: "include" },
    );
    if (!res.ok) {
      toast({
        title: "Download failed",
        description: describeError(new Error(await res.text())),
        variant: "destructive",
      });
      return;
    }
    const data = (await res.json()) as { downloadUrl: string };
    window.open(data.downloadUrl, "_blank", "noopener,noreferrer");
  };

  const downloadSelected = async () => {
    const fileIds = Array.from(selected);
    setZipPending(true);
    try {
      const res = await fetch("/api/files/download-zip", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId,
          fileIds,
          probe: true,
        }),
      });
      if (!res.ok) {
        throw new Error(`${res.status}: ${await res.text()}`);
      }
      submitNativeZipDownload(clientId, fileIds);
      toast({ title: "Download started" });
    } catch (error) {
      toast({
        title: "Download failed",
        description: describeError(error),
        variant: "destructive",
      });
    } finally {
      setZipPending(false);
    }
  };

  const requestRemoveFolder = () => {
    if (!activeFolder || !canRemoveFolder) return;
    if (activeFolder.isPartnerFolder) {
      toast({
        title: "Cannot remove folder",
        description:
          "This folder belongs to a supplier and cannot be removed here.",
        variant: "destructive",
      });
      return;
    }
    if (files.length > 0) {
      toast({
        title: "Cannot remove folder",
        description: `This folder still has ${files.length} file${files.length === 1 ? "" : "s"}. Move or delete them first.`,
        variant: "destructive",
      });
      return;
    }
    setRemoveOpen(true);
  };

  const folderHint = !activeFolder
    ? null
    : activeFolder.kind === "inbox" || activeFolder.kind === "archive"
      ? "Inbox and Archive are built-in folders."
      : activeFolder.isPartnerFolder
        ? "Supplier folder. Renaming it also renames the supplier. It cannot be removed here."
        : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Client Files"
        description="Browse uploads, rename folders, archive or permanently delete files, and download a selection."
        action={
          <div className="flex flex-wrap gap-2">
            <Link href="~/admin/settings?tab=files">
              <Button type="button" variant="outline">
                <Settings className="mr-2 h-4 w-4" />
                Notification settings
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              disabled={applyCorsMutation.isPending}
              onClick={() => applyCorsMutation.mutate()}
            >
              {applyCorsMutation.isPending ? "Applying CORS…" : "Apply R2 CORS"}
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Client</Label>
          <Select
            value={clientId}
            onValueChange={(value) => {
              setClientId(value);
              setFolderId("");
              setSelected(new Set());
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.clientId} value={client.clientId}>
                  {client.clientName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Folder</Label>
          <Select
            value={activeFolderId}
            onValueChange={(value) => {
              setFolderId(value);
              setSelected(new Set());
            }}
            disabled={!clientId || !folders.length}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select folder" />
            </SelectTrigger>
            <SelectContent>
              {folders.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>
                  {folder.name}
                  {folder.isPartnerFolder ? " (supplier)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {clientId && activeFolder && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canRenameFolder || renameFolderMutation.isPending}
              onClick={() => {
                setRenameValue(activeFolder.name);
                setRenameOpen(true);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Rename
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canRemoveFolder || removeFolderMutation.isPending}
              onClick={requestRemoveFolder}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove
            </Button>
          </div>
          {folderHint && (
            <p className="text-sm text-muted-foreground">{folderHint}</p>
          )}
        </div>
      )}

      {clientId && (
        <Card>
          <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="new-folder">New folder</Label>
              <Input
                id="new-folder"
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder="e.g. Product images"
              />
            </div>
            <Button
              type="button"
              disabled={!newFolderName.trim() || createFolderMutation.isPending}
              onClick={() => createFolderMutation.mutate()}
            >
              <FolderPlus className="mr-2 h-4 w-4" />
              Create
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {files.length > 0 && (
                <Checkbox
                  checked={
                    headerSelection === "all"
                      ? true
                      : headerSelection === "some"
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={() => {
                    setSelected((current) =>
                      toggleVisibleSelection(visibleIds, current),
                    );
                  }}
                  aria-label="Select all files"
                />
              )}
              <p className="text-sm text-muted-foreground">
                {files.length} file{files.length === 1 ? "" : "s"}
                {selected.size > 0 ? ` · ${selected.size} selected` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!selected.size || zipPending}
                onClick={() => void downloadSelected()}
              >
                <Download className="mr-2 h-4 w-4" />
                {zipPending ? "Preparing zip…" : "Download selected"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={
                  !selected.size ||
                  deleteOpen ||
                  deleteMutation.isPending ||
                  archiveMutation.isPending
                }
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete selected
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={
                  !selected.size ||
                  deleteOpen ||
                  deleteMutation.isPending ||
                  archiveMutation.isPending
                }
                onClick={() => archiveMutation.mutate()}
              >
                <Archive className="mr-2 h-4 w-4" />
                Move selected to Archive
              </Button>
            </div>
          </div>

          {!clientId ? (
            <p className="text-sm text-muted-foreground">Select a client to begin.</p>
          ) : isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !files.length ? (
            <p className="text-sm text-muted-foreground">No files in this folder.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {files.map((file) => {
                const checked = selected.has(file.id);
                return (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 px-3 py-2"
                  >
                    <Checkbox
                      checked={checked}
                      aria-label={`Select ${file.relativePath}`}
                      onCheckedChange={(value) => {
                        setSelected((current) => {
                          const next = new Set(current);
                          if (value === true) next.add(file.id);
                          else next.delete(file.id);
                          return next;
                        });
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {file.relativePath}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatBytes(file.sizeBytes)} ·{" "}
                        {formatDateTime(file.createdAt)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => downloadFile(file.id)}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Download
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
            <DialogDescription>
              {activeFolder?.isPartnerFolder
                ? "This also renames the supplier. Files stay in the same storage location."
                : "The folder name changes. Files stay in the same storage location."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-folder">Name</Label>
            <Input
              id="rename-folder"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!renameValue.trim() || renameFolderMutation.isPending}
              onClick={() => renameFolderMutation.mutate()}
            >
              {renameFolderMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {activeFolder?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the empty folder. It cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              onClick={(event) => {
                event.preventDefault();
                removeFolderMutation.mutate();
              }}
            >
              {removeFolderMutation.isPending ? "Removing…" : "Remove folder"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Permanently delete {selected.size} file
              {selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the files from storage. It cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={deleteMutation.isPending || archiveMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (deleteMutation.isPending || archiveMutation.isPending) return;
                deleteMutation.mutate();
              }}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete files"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
